import React, { createContext, useState, useContext, useMemo } from 'react';

// --- 1. Import BOTH static data files ---
import productData from '../data/products.json';
import menuData from '../data/menuDrinks.json';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // --- 2. Combine both product lists into one for easy lookup ---
  const allStockedItems = useMemo(() => [
    ...productData.products,
    ...menuData.menuDrinks
  ], []);

  // Helper function to get stock from the STATIC list
  const getStaticStock = (itemId) => {
    const item = allStockedItems.find(p => p.id === itemId);
    // If the item exists and has a 'stock' property, return it.
    // Otherwise, assume infinite stock (for menu drinks without stock).
    return item?.stock ?? Infinity;
  };

  const addToCart = (product) => {
    const maxStock = getStaticStock(product.id);

    setCartItems(prevItems => {
      const itemExists = prevItems.find(item => item.id === product.id);

      if (itemExists) {
        if (itemExists.quantity < maxStock) {
          return prevItems.map(item =>
            item.id === product.id ? { ...item, quantity: item.quantity + 1, stock: maxStock } : item
          );
        }
        return prevItems; // Max stock reached
      }
      
      if (maxStock > 0) {
        // Add the stock property when adding the item for the first time
        return [...prevItems, { ...product, quantity: 1, stock: maxStock }];
      }
      return prevItems; // Out of stock
    });
  };

  const increaseQuantity = (itemId) => {
    const maxStock = getStaticStock(itemId);
    setCartItems(prevItems =>
      prevItems.map(item => {
        if (item.id === itemId) {
          const newQuantity = Math.min(item.quantity + 1, maxStock);
          return { ...item, quantity: newQuantity, stock: maxStock };
        }
        return item;
      })
    );
  };
  
  const decreaseQuantity = (productId) => { /* ... no changes needed ... */ };
  const removeFromCart = (productId) => { /* ... no changes needed ... */ };
  const clearCart = () => { setCartItems([]); };
  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  const value = {
    cartItems, isCartOpen, addToCart, increaseQuantity,
    decreaseQuantity, removeFromCart, clearCart, openCart, closeCart,
  };
 
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};